import { useEffect } from 'react';

export enum MetricTypes {
  // 首帧播放耗时（s）
  firstFrameLoadTime = 'first_frame_load_time',
  // 当前播放资源地址
  videoUrl = 'video_url',
  // 视频总时长（s）
  videoDuration = 'video_duration',
  // 点赞状态
  likeStatus = 'like_status',
  // 收藏状态
  collectStatus = 'collect_status',
  // 评论状态
  commentStatus = 'comment_status',
  // 失败原因
  failReason = 'fail_reason',
  // 图片上传状态
  imageUploadStatus = 'image_upload_status',
  // 视频上传状态
  videoUploadStatus = 'video_upload_status',
  // 个人中心页SSR渲染总耗时
  userCenterSsrRenderTime = 'user_center_ssr_render_time',
  // 获取登录信息接口耗时
  userCenterLoginInfoApiTime = 'user_center_login_info_api_time',
  // 积分兑换接口耗时
  userCreditsExchangeApplyApiTime = 'user_credits_exchange_apply_api_time',
  // 头像上传接口耗时
  userAvatarUploadApiTime = 'user_avatar_upload_api_time',
}

export enum statusTypes {
  // 点击
  click = 1,
  // 成功
  success = 2,
  // 失败
  fail = 3,
  // 非错误导致的未成功(如: 未登录/未绑定手机号)
  unErrorFail = 4,
}

type SlsDataType = 'setEnvironment' | 'reportMetric' | 'reportNativeApi' | 'reportSentryError';
// reportMetric 的 key 是字符串 value 必须是number类型
export type SlsData = 
  | [Exclude<SlsDataType, 'reportMetric'>, any] 
  | ['reportMetric', string, number];

const METRIC_ENV_CLEANUP = {
  page_route: '',
  route_params: '',
  uid: '',
  terminal_type: '',
  user_center_view_type: '',
  ssr_duration: '',
  api_path: '',
  api_params: '',
  is_success: '',
  fail_reason: '',
  response_code: '',
  stage: '',
  scene: '',
};

const useSls = () => {
  const loadSLsScript = () => {
    window.sls = window.sls || [];
    window.sls.push([
      'config',
      {
        urlHandler: () => {
          const { origin, pathname } = window.location;
          return `${origin}${pathname}`;
        },
      },
    ]);
    const s: HTMLScriptElement = document.createElement('script');
    s.src = process.env.SLS_SDK!;
    s.async = true;
    s.id = 'sls-monitor';
    s.setAttribute('data-sls-host', process.env.SLS_HOST!);
    s.setAttribute('data-sls-project',  process.env.SLS_PROJECT!);
    s.setAttribute('data-sls-log-store',  process.env.SLS_LOGSTORE!);
    s.setAttribute(
      'data-sls-sampling-rate',
      process.env.ENV === 'prod' ? '0.1' : '1'
    );
    setTimeout(() => {
      document.body.appendChild(s);
    }, 2000);
  };

  // 自定义上报函数
  const reportSlsData = (slsData: Array<SlsData>) => {
    if (!window.sls || slsData.length == 0) return
    // 文档地址：https://fe.djicorp.com/specifications/ops/performance/2-sdk.html
    
    const href = window.location.href;
    const metricTuple = slsData.find((item): item is ['reportMetric', string, number] => item[0] === 'reportMetric');
    const metricName = metricTuple?.[1] || '';
    const isNoHrefMetric =
      metricName === MetricTypes.userCreditsExchangeApplyApiTime ||
      metricName === MetricTypes.userAvatarUploadApiTime;
    const shouldCleanupMetricEnv =
      metricName === MetricTypes.userCenterSsrRenderTime ||
      metricName === MetricTypes.userCenterLoginInfoApiTime;
    const shouldInjectHref = !isNoHrefMetric;
    // 判断是否存在 setEnvironment
    const hasSetEnvironment = slsData.some(item => item[0] === 'setEnvironment');
    
    // 重新排序
    let reorderedData;

    if (hasSetEnvironment) {
      // 修改现有的 setEnvironment 数据，添加 href 参数
      // 修复 urlHandler 对url 聚合后 无法获取完整 url 的问题
      reorderedData = slsData.map(item => {
        if (item[0] === 'setEnvironment') {
          return ['setEnvironment', shouldInjectHref ? { href, ...item[1] } : item[1]];
        }
        return item;
      });
    } else {
      // 如果没有 setEnvironment，则新增一个包含 href 的 setEnvironment
      reorderedData = [
        ...(shouldInjectHref ? [['setEnvironment', { href }] as SlsData] : []),
        ...slsData,
      ];
    }

    // 确保 setEnvironment 始终在数组最前面
    reorderedData = [
      ...reorderedData.filter(item => item[0] === 'setEnvironment'),
      ...reorderedData.filter(item => item[0] !== 'setEnvironment')
    ];
    
    window.sls.push(...reorderedData);
    if (shouldCleanupMetricEnv) {
      window.sls.push(['setEnvironment', { ...METRIC_ENV_CLEANUP, href }]);
    }
  };

  // 初始化 sls 脚本
  useEffect(() => {
    const slsEle = document.querySelector('#sls-monitor');
    if(!slsEle) {
      loadSLsScript();
    }
  }, []);

  return {
    reportSlsData
  }
};

export default useSls;
